import {
  Caption1,
  Combobox,
  Field,
  makeStyles,
  Option,
  Spinner,
  Text,
  tokens,
  useId,
} from '@fluentui/react-components';
import {
  type ReactNode,
  type UIEvent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { LockedSelection } from '#src/shared/ui/LockedSelection';
import { useDebouncedValue } from '#src/shared/useDebouncedValue';

/** Distância do fim da lista em que a próxima página já é pedida. */
const LOAD_MORE_THRESHOLD_PX = 48;
const STATUS_VALUE = '__status__';

const useStyles = makeStyles({
  // O Combobox do Fluent tem 250px de largura mínima: numa coluna mais
  // estreita (filtros em duas colunas), passaria da borda.
  combobox: { width: '100%', minWidth: 0 },
  // Uns 10 itens de uma linha à vista (uma página da busca); o resto vem
  // rolando, e carregando mais, se houver.
  listbox: { maxHeight: '344px', overflowY: 'auto' },
  option: { display: 'flex', flexDirection: 'column', minWidth: 0 },
  secondary: { color: tokens.colorNeutralForeground3 },
  status: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
  },
});

/** O que o combobox precisa da busca, venha ela de onde vier. */
export interface AsyncOptions<T> {
  readonly items: readonly T[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly hasMore: boolean;
  /** Por que ainda não há busca (`Digite ao menos 2 letras`). */
  readonly idleMessage?: string | null;
  loadMore(): void;
  retry(): void;
}

export interface AsyncComboboxProps<T> {
  readonly label: string;
  readonly placeholder: string;
  readonly value: T | null;
  onChange(value: T | null): void;
  /**
   * Hook que busca as opções para o texto digitado (já com debounce). Precisa
   * ser a mesma função a cada render, como todo hook.
   */
  readonly useOptions: (text: string) => AsyncOptions<T>;
  getKey(item: T): string;
  getLabel(item: T): string;
  getDescription?(item: T): string | undefined;
  /** Motivo para a opção não poder ser escolhida (`Já tem acesso`). */
  disabledReason?(item: T): string | null;
  readonly icon?: ReactNode;
  readonly required?: boolean;
  readonly hint?: string | null;
  readonly validationMessage?: string | null;
  readonly disabled?: boolean;
  readonly emptyText?: string;
}

function StatusRow({
  isLoading,
  error,
  total,
  emptyText,
  idleMessage,
}: {
  isLoading: boolean;
  error: Error | null;
  total: number;
  emptyText: string;
  idleMessage: string | null;
}) {
  const styles = useStyles();
  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className={styles.status}>
        <Spinner size="tiny" />
        <Caption1>Buscando...</Caption1>
      </div>
    );
  }
  const message =
    error !== null
      ? `Não foi possível buscar: ${error.message}`
      : total === 0
        ? (idleMessage ?? emptyText)
        : null;
  return message === null ? null : (
    <Option disabled value={STATUS_VALUE} text={message}>
      <Caption1 className={styles.secondary}>{message}</Caption1>
    </Option>
  );
}

/**
 * Pede a próxima página ao rolar perto do fim, e também quando a lista não
 * enche a caixa (sem barra de rolagem, a rolagem nunca aconteceria).
 */
function useLoadMore(
  options: AsyncOptions<unknown>,
  open: boolean,
  listboxId: string,
) {
  const { hasMore, isLoading, loadMore } = options;
  useEffect(() => {
    const element = document.getElementById(listboxId);
    if (
      open &&
      hasMore &&
      !isLoading &&
      element !== null &&
      element.scrollHeight <= element.clientHeight
    ) {
      loadMore();
    }
  }, [open, hasMore, isLoading, loadMore, listboxId]);

  return (event: UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const nearEnd =
      scrollHeight - scrollTop - clientHeight < LOAD_MORE_THRESHOLD_PX;
    if (nearEnd && hasMore && !isLoading) {
      loadMore();
    }
  };
}

function OptionRow<T>({
  item,
  props,
}: {
  item: T;
  props: AsyncComboboxProps<T>;
}) {
  const styles = useStyles();
  const label = props.getLabel(item);
  const secondary =
    props.disabledReason?.(item) ?? props.getDescription?.(item);
  return (
    <Option
      value={props.getKey(item)}
      text={label}
      disabled={(props.disabledReason?.(item) ?? null) !== null}
    >
      <div className={styles.option}>
        <Text truncate wrap={false}>
          {label}
        </Text>
        {secondary && (
          <Caption1 truncate wrap={false} className={styles.secondary}>
            {secondary}
          </Caption1>
        )}
      </div>
    </Option>
  );
}

/**
 * Rótulo, dica e erro do campo. O `Field` liga o rótulo ao controle injetando
 * `id` e `aria-*` no filho direto: por isso ele envolve o próprio Combobox.
 */
function FieldFrame<T>({
  props,
  children,
}: {
  props: AsyncComboboxProps<T>;
  children: ReactNode;
}) {
  return (
    <Field
      label={props.label}
      required={props.required ?? false}
      hint={props.hint ?? null}
      validationMessage={props.validationMessage ?? null}
    >
      {children}
    </Field>
  );
}

/** A busca aberta: texto, lista do servidor e status. */
function SearchCombobox<T>(props: AsyncComboboxProps<T>) {
  const styles = useStyles();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  // O Fluent não tipa a ref do listbox; o id basta para medir a lista.
  const listboxId = useId('async-combobox-listbox');
  const options = props.useOptions(useDebouncedValue(text.trim()));
  const onScroll = useLoadMore(options, open, listboxId);
  const { getKey, onChange } = props;
  const byKey = useMemo(
    () => new Map(options.items.map((item) => [getKey(item), item])),
    [options.items, getKey],
  );

  return (
    <FieldFrame props={props}>
      <Combobox
        className={styles.combobox}
        freeform
        placeholder={props.placeholder}
        value={text}
        open={open}
        disabled={props.disabled ?? false}
        onOpenChange={(_, data) => {
          setOpen(data.open);
          if (data.open && options.error !== null) {
            options.retry();
          }
        }}
        onChange={(event) => {
          setText(event.target.value);
          setOpen(true);
        }}
        onOptionSelect={(_, data) => {
          const item = byKey.get(data.optionValue ?? '');
          if (item !== undefined) {
            onChange(item);
          }
        }}
        listbox={{ id: listboxId, onScroll, className: styles.listbox }}
        // Sem autoSize: ele grava um max-height inline do tamanho da tela, que
        // passa por cima do limite acima. Sem espaço embaixo, a lista abre
        // para cima.
        positioning={{ autoSize: false }}
      >
        {options.items.map((item) => (
          <OptionRow key={getKey(item)} item={item} props={props} />
        ))}
        <StatusRow
          isLoading={options.isLoading}
          error={options.error}
          total={options.items.length}
          emptyText={props.emptyText ?? 'Nada encontrado.'}
          idleMessage={options.idleMessage ?? null}
        />
      </Combobox>
    </FieldFrame>
  );
}

/**
 * Campo de busca ligado à API: a lista vem do servidor conforme a pessoa
 * digita, carrega mais ao rolar, e o item escolhido fica travado num cartão
 * (`LockedSelection`) até a pessoa pedir para trocar.
 */
export function AsyncCombobox<T>(props: AsyncComboboxProps<T>) {
  const { value, onChange } = props;
  if (value === null) {
    return <SearchCombobox {...props} />;
  }
  return (
    <FieldFrame props={props}>
      <LockedSelection
        label={props.getLabel(value)}
        description={props.getDescription?.(value)}
        icon={props.icon}
        clearLabel={`Trocar ${props.label.toLowerCase()}`}
        onClear={props.disabled ? undefined : () => onChange(null)}
      />
    </FieldFrame>
  );
}
